import React from 'react';
import { Box, Button, CircularProgress, type ButtonProps } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * 带全铺加载遮罩的 MUI 按钮，用于弹窗底部主操作等；`loading` 由父层与接口提交状态同步。
 * 交互与视觉对齐 `waitButton.component.tsx`：禁用点击、半透明遮罩、居中进度圈。
 */
export type LoadingOverlayButtonProps = Omit<ButtonProps, 'children'> & {
    /** 为 true 时展示遮罩与进度圈，并禁用点击 */
    loading: boolean;
    /** 按钮主文案 */
    children: React.ReactNode;
    /** 加载指示器直径（px），与原先 startIcon 内 CircularProgress 对齐时可用 22 */
    loadingIndicatorSize?: number;
};

/**
 * 渲染带加载遮罩的按钮；非加载态仍可使用 `startIcon` 展示左侧图标。
 */
export default function LoadingOverlayButton(props: LoadingOverlayButtonProps): JSX.Element {
    const { loading, children, disabled, startIcon, sx, loadingIndicatorSize = 20, ...rest } = props;

    return (
        <Button
            disabled={disabled || loading}
            startIcon={startIcon}
            sx={[
                {
                    position: 'relative',
                    overflow: 'hidden',
                },
                ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
            ]}
            {...rest}
        >
            {loading ? (
                <Box
                    aria-hidden
                    sx={(theme) => ({
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        borderRadius: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.common.white, 0.2),
                    })}
                >
                    <CircularProgress color="primary" size={loadingIndicatorSize} thickness={4} />
                </Box>
            ) : null}
            {children}
        </Button>
    );
}
